// #795: 匿名プレイヤーがプレイを終えて離れるときに、サインアップ CTA を 1 枚だけ差し込む。
//
// 判定は WorkPlay が自分で持っている情報だけで完結させる。close_game / clear_game は
// 到達率が低く（prod 実測で匿名プレイの 15.6%）、Pyxel は cross-origin の Pyodide なので
// クリアも機械的に取れない（#748/#749）。使うのは滞在時間・SPA セッション内の再生回数・
// 認証状態の 3 つだけ。
//
// 実体の <PlaySignupCtaDialog> は app.vue に 1 度だけマウントする（#751 と同じ形）。
// WorkPlay はページ遷移で unmount するので、カウンタも開閉状態もコンポーネントの外に置く。
//
// #821: 「いつ呼ぶか」は WorkPlay 側の関心。呼び出し元はヘッダーの「戻る」ボタンだけ
// だったが、SPA のルート遷移（作者リンク・ブラウザバック）でも呼ぶようになった。
// このモジュールの判定条件と 1 ブラウザ 1 日 1 回の上限は変えていない — `writeSuppressedOn`
// は表示時に必ず走るので、発火点を増やしてもうるささは増えない。

/**
 * 「十分に遊んだ」とみなす滞在時間。prod 実測（30 日）で、戻る操作をした匿名プレイヤーの
 * 滞在は中央値 22 秒。15 秒なら Pyodide の起動（体感 5〜10 秒）を差し引いても実プレイが
 * 残り、かつこの層の 60% に届く。
 */
export const PLAY_SIGNUP_CTA_DWELL_MS = 15_000

const SUPPRESS_KEY = 'playSignupCtaSuppressedOn'

/**
 * #844: 未認証でも作品を回遊できる公開ギャラリー（#843）。
 *
 * ここに置くのは、この定数の意味が「**離脱ではなく回遊とみなす行き先**」だから。
 * プレイ面から見た「出口」と「CTA を出さない条件」は同じ 1 つの判断なので、
 * 2 箇所に literal で散らすと片方だけ直したときに CTA が回遊に割り込む。
 */
export const PUBLIC_GALLERY_PATH = '/works'

export type PlaySignupCtaTrigger = 'dwell' | 'repeat'

export interface PlaySignupCtaInput {
  authenticated: boolean
  shownInSession: boolean
  suppressedToday: boolean
  /** SPA セッション内で何作品目か（1 始まり）。 */
  playCount: number
  dwellMs: number
}

/**
 * 離脱の瞬間に CTA を出すべきかを判定する。出すなら効いたトリガー、出さないなら null。
 * `repeat` を先に見るのは、回遊層のほうが純度の高いシグナルで、滞在時間を満たさなくても
 * 出したいため。
 */
export function resolvePlaySignupCtaTrigger(
  input: PlaySignupCtaInput,
): PlaySignupCtaTrigger | null {
  if (input.authenticated || input.shownInSession || input.suppressedToday) return null
  if (input.playCount >= 2) return 'repeat'
  if (input.dwellMs >= PLAY_SIGNUP_CTA_DWELL_MS) return 'dwell'
  return null
}

// 日付はローカル時刻で持つ。UTC だと JST の 09:00 で抑止が切れる。
const localDateKey = () => {
  const d = new Date()
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

// 公開ページなのでストレージ無効化（cookie ブロック等）を踏みうる。読み書きの失敗で
// 戻るボタンごと壊さないよう握りつぶす。
const readSuppressedOn = (): string | null => {
  try {
    return window.localStorage.getItem(SUPPRESS_KEY)
  }
  catch {
    return null
  }
}

const writeSuppressedOn = (value: string) => {
  try {
    window.localStorage.setItem(SUPPRESS_KEY, value)
  }
  catch {
    // noop
  }
}

export function usePlaySignupCta() {
  const isOpen = useState<boolean>('playSignupCtaOpen', () => false)
  const trigger = useState<PlaySignupCtaTrigger>('playSignupCtaTrigger', () => 'dwell')
  const playCount = useState<number>('playSignupCtaPlayCount', () => 0)
  const shownInSession = useState<boolean>('playSignupCtaShown', () => false)
  // 「今はしない」で閉じたことを WorkPlay に伝えるカウンタ。保留していた離脱を実行させる。
  // コールバックを useState に入れられないのでシリアライズ可能な形にしてある。
  const dismissCount = useState<number>('playSignupCtaDismissCount', () => 0)

  const authInfoStore = useAuthInfoStore()
  const { trackPlaySignupCta, trackPlayMoreCta } = useFunnelEvent()

  const notePlayStart = () => {
    playCount.value += 1
  }

  /** 離脱の瞬間に呼ぶ。CTA を開いたら true（呼び出し側は離脱を保留する）。 */
  const maybeOpenOnLeave = (dwellMs: number): boolean => {
    const t = resolvePlaySignupCtaTrigger({
      authenticated: authInfoStore.isAuthenticated,
      shownInSession: shownInSession.value,
      suppressedToday: readSuppressedOn() === localDateKey(),
      playCount: playCount.value,
      dwellMs,
    })
    if (!t) return false

    trigger.value = t
    shownInSession.value = true
    writeSuppressedOn(localDateKey())
    isOpen.value = true
    trackPlaySignupCta('shown', t)
    // ダイアログは 2 つの出口（回遊 / 登録）を並べるので、露出も 2 つ数える。
    trackPlayMoreCta('shown', 'leave_dialog')
    return true
  }

  const dismiss = () => {
    isOpen.value = false
    trackPlaySignupCta('dismissed', trigger.value)
    dismissCount.value += 1
  }

  const accept = () => {
    isOpen.value = false
    trackPlaySignupCta('clicked', trigger.value)
  }

  /**
   * 「ほかの作品を見る」。**保留していた離脱は再開しない** —— 呼び出し側がギャラリーへ
   * 遷移するので、そこで改めて guard が走る（`shownInSession` が立っているので
   * 2 度目は出ない）。`dismissCount` を進めないのはそのため。
   */
  const browse = () => {
    isOpen.value = false
    trackPlayMoreCta('clicked', 'leave_dialog')
  }

  return { isOpen, trigger, dismissCount, notePlayStart, maybeOpenOnLeave, dismiss, accept, browse }
}
